import './module-template-page.css';

import classNames from 'classnames';
import { Link } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import ReactPlayer from 'react-player';

import { BlogPortableText } from '../../components/blog-portable-text';
import { Cards } from '../../components/cards';
import { LearnMore } from '../../components/learnMore';
import { SEO } from '../../components/seo';
import { Stacks } from '../../components/stacks';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import * as styles from './ungated-content-layout.module.css';

const UngatedContentLayout = ({ resource, resourceCTA, siteSettings }) => {
	// hooks
	const isMobile = useMediaQuery('(max-width: 799px)');

	// set up resource data
	const description = resource?.description;
	const dynamicContent = resource?.resourceDynamicContent;
	const hasDynamicContent = resource?.hasDynamicContent;
	const image = getImage(resource?.image?.asset);
	const openGraph = resource?.openGraph;
	const relatedResources = resource?.relatedResources;
	const resourceContent = resource?._rawResourceContent;
	const resourceType = resource?.resourceType[0]?.title;
	const tags = resource?.tags;
	const title = resource?.title;
	const topic = resource?.topics[0];
	const video = resource?.video;

	// seo data
	const pageTitle = openGraph?.title || siteSettings.openGraph.title;

	const seoImageUrl =
		openGraph?.image?.asset?.url || siteSettings.openGraph.image.asset.url;

	const pageDescription = openGraph?.description || siteSettings.description;

	return (
		<div>
			<SEO
				description={pageDescription}
				keywords={siteSettings.keywords}
				imageUrl={seoImageUrl}
				title={pageTitle}
			/>
			<div
				className={classNames(
					'manual-header-spacer',
					'responsive-layout',
					'resource-template',
					styles.moduleContainer
				)}
			>
				{/* hero */}
				<div
					className={classNames(
						'full-width-container',
						'hero-container'
					)}
				>
					<div
						className={classNames(
							'content-container',
							styles.heroLayoutContainer
						)}
					>
						{topic && (
							<p className={isMobile ? 'body-m' : 'body-xl'}>
								<strong>
									<Link to='/for-providers/resources/'>
										Provider Resources
									</Link>
									<span className='topic-breadcrumb-arrow'>
										{` › `}
									</span>
									<Link
										to={`/for-providers/resources/topics/${topic?.slug?.current}`}
									>
										<span className='topic-breadcrumb'>
											{topic?.title}
										</span>
									</Link>
								</strong>
							</p>
						)}
						<div
							className={classNames(
								styles.heroContentContainer,
								'hero-content-container'
							)}
						>
							<div className='hero-content'>
								{resourceType && (
									<p
										className={classNames(
											'heading-xs',
											styles.contentType
										)}
									>
										{resourceType}
									</p>
								)}
								{title && (
									<h1
										className={classNames(
											'heading-xl',
											styles.contentTitle
										)}
									>
										{title}
									</h1>
								)}
								{description && (
									<p
										className={classNames(
											'body-xl',
											styles.contentDescription
										)}
									>
										{description}
									</p>
								)}
							</div>
							<div>
								{image && !video && (
									<GatsbyImage
										image={image}
										alt={title || 'Resource Image'}
									/>
								)}
								{video && (
									<ReactPlayer
										className={`react-player ${styles.reactPlayer}`}
										controls
										width='100%'
										height='auto'
										url={video.url}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
				{/* content */}
				<div
					className={classNames(
						'full-width-container',
						'content-container-outer'
					)}
				>
					<div
						className={classNames(
							'content-container',
							'content-layout'
						)}
					>
						<div className='col col-c-8'>
							{resourceContent && (
								<div className='description-wrapper'>
									<BlogPortableText
										blocks={resource?._rawResourceContent}
									/>
								</div>
							)}
							<div />
							{tags && tags.length > 0 && (
								<div className='tags-container'>
									<p className={'body-m tags-title'}>Tags:</p>
									<ul className='tags-list'>
										{tags.map((tag) => {
											const { _id, slug, title } = tag;

											return (
												<Link
													key={_id}
													to={`/for-providers/resources/tags/${slug.current}`}
												>
													<li className='tag'>
														<p className='body-s'>
															{title}
														</p>
													</li>
												</Link>
											);
										})}
									</ul>
								</div>
							)}
						</div>
					</div>
				</div>
				{/* dynamic content */}
				{hasDynamicContent && (
					<div
						className={classNames(
							'dynamicContentContainer',
							'full-width-container'
						)}
					>
						{dynamicContent?.map((content) => {
							const { _type } = content;

							switch (_type) {
								case 'stacks':
									return (
										<Stacks
											key={content._key}
											{...content}
										/>
									);
								case 'cards':
									return (
										<Cards
											key={content._key}
											{...content}
										/>
									);
								default:
									return null;
							}
						})}
					</div>
				)}
				{/* related resources */}
				{relatedResources && relatedResources.length > 0 && (
					<div
						className={classNames(
							'full-width-container',
							'relatedResourcesContainer'
						)}
					>
						<div
							className={classNames(
								'content-container',
								'relatedResourcesLayout'
							)}
						>
							<h2 className='heading-l'>Related resources</h2>
							<div className='relatedResources'>
								{relatedResources?.map((relatedResource) => {
									const {
										_id,
										image,
										pageType,
										resourceType,
										slug,
										title,
									} = relatedResource;

									const resourceTypeTitle =
										resourceType[0]?.title;
									const relatedResourceImage = getImage(
										image?.asset
									);

									// set up resource url
									const isBlogPost = pageType === 'blogPost';

									const blogUrl = `/blog/${slug.current}`;
									const resourceUrl = `/for-providers/resources/${slug.current}`;

									const resourceLink = isBlogPost
										? blogUrl
										: resourceUrl;

									return (
										<a key={_id} href={resourceLink}>
											<div className='relatedResource'>
												{relatedResourceImage && (
													<GatsbyImage
														image={
															relatedResourceImage
														}
														alt={
															title ||
															'Related Resource Image'
														}
													/>
												)}
												<div className='relatedResourceContent'>
													<p className='body-sm-caps'>
														{resourceTypeTitle}
													</p>
													<p className='body-display-sm'>
														{title}
													</p>
												</div>
											</div>
										</a>
									);
								})}
							</div>
						</div>
					</div>
				)}
				{/* cta */}
				{resourceCTA && (
					<div
						className={classNames(
							'full-width-container',
							styles.ctaContainer
						)}
					>
						<LearnMore {...resourceCTA} />
					</div>
				)}
			</div>
		</div>
	);
};

export default UngatedContentLayout;
